const sdkPthreadBootstrapCandidates = [
  '/static/js/ouster_js_web.js',
  '/index.js',
];

type ResolvedSdkPthreadBootstrap = {
  bootstrapUrl: string;
  source: string;
};

let sdkPthreadBootstrapSourcePromise: Promise<ResolvedSdkPthreadBootstrap> | null =
  null;

const isSdkBootstrapSource = (source: string): boolean =>
  source.includes('var loadOusterSDK=') ||
  source.includes('loadOusterSDK=(()=>');

// Disables the SDK's pthread pool so it runs single-threaded instead of
// spawning real pthread Workers sharing WASM linear memory - shared here so
// every caller that needs this stays in sync with the vendored source's
// exact variable declaration.
export const disablePthreadPool = (source: string): string =>
  source.replace('var pthreadPoolSize=4;', 'var pthreadPoolSize=0;');

export const resolveSdkPthreadBootstrapSource =
  async (): Promise<ResolvedSdkPthreadBootstrap> => {
    if (!sdkPthreadBootstrapSourcePromise) {
      sdkPthreadBootstrapSourcePromise = (async () => {
        const tryResolveCandidate = async (
          index: number
        ): Promise<ResolvedSdkPthreadBootstrap> => {
          const bootstrapUrl = sdkPthreadBootstrapCandidates[index];
          if (!bootstrapUrl) {
            throw new Error('Failed to resolve pthread bootstrap');
          }

          try {
            const response = await fetch(bootstrapUrl, { cache: 'no-store' });
            if (!response.ok) {
              throw new Error(
                `Failed to fetch pthread bootstrap: ${response.status}`
              );
            }

            const source = await response.text();
            if (!isSdkBootstrapSource(source)) {
              throw new Error(
                'Fetched bootstrap candidate did not contain Ouster SDK source'
              );
            }

            return { bootstrapUrl, source };
          } catch (error) {
            const resolvedError =
              error instanceof Error ? error : new Error(String(error));

            const hasNextCandidate =
              index + 1 < sdkPthreadBootstrapCandidates.length;
            if (!hasNextCandidate) {
              throw resolvedError;
            }

            return tryResolveCandidate(index + 1);
          }
        };

        return tryResolveCandidate(0);
      })();
    }

    return sdkPthreadBootstrapSourcePromise;
  };
